import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { EditableTable, EditableColumnDef } from '../../../../../../shared/components/table/inline-edit-table';
import { useTransactionCertificateStore } from '../../../../../../store/transaction-certificates.store';
import { Utills } from '../../../../../../shared/app-configs/utilit';
import { useApi } from '../../../../../../shared/hooks/useApi';
import { TCAppConfig } from '../../../../../../shared/app-configs/tc-app-configs';
import { CustomTextField, CustomSelect, CustomDatePicker } from '../../../../../../shared/components/inputfields/form-elements';

// Combined data interface for table rows
interface CombinedProductData {
  id: string;
  productId: string;
  // Product Details
  inputTcProductNo: string;
  inputTcProductArticleNo: string;
  inputTcProductNumberOfUnits: number | null;
  inputTcProductUnitsMetric: string;
  inputTcProductProductionDate: string;
  inputTcProductCategoryCode: string;
  inputTcProductCategoryUserSpecificTerm: string;
  inputTcProductDetailCode: string;
  inputTcProductDetailUserSpecificTerm: string;
  // Additional Details
  inputTcProductInfoTrimsExcluded: string;
  inputTcProductInfoAssociatedTc: string;
  inputTcProductInfoFiberLength: string;
  inputTcProductInfoYarnCount: string;
  inputTcProductInfoFabricWeightAndConstruct: string;
  inputTcProductInfoFabricWeightOfFinishedProduct: string;
  inputTcProductInfoAdditional: string;
}

interface CombinedProductDetailsProps {
  data: CombinedProductData[];
  selectedProductIds: string[];
  onDataChange: (updatedData: CombinedProductData[]) => void;
  onSelectionChange: (selectedIndices: number[]) => void;
}

const CombinedProductDetails: React.FC<CombinedProductDetailsProps> = ({
  data,
  selectedProductIds,
  onDataChange,
  onSelectionChange,
}) => {
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editingRowIndices, setEditingRowIndices] = useState<number[]>([]);
  const [productCategoryCode, setProductCategoryCode] = useState([]);
  const [productDetailsCode, setProductDetailsCode] = useState([]);
  const { setProductCategoriesStore, setProductDetailCodeStore } = useTransactionCertificateStore();


  // Convert selected product IDs to row indices
  const getSelectedRowIndices = (): number[] => {
    const indices = data
      .map((product, index) => {
        const isSelected = selectedProductIds.includes(product.productId);
        return isSelected ? index : -1;
      })
      .filter(index => index !== -1);
    
    return indices;
  };

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  // Load dropdown options
  useEffect(() => {
    const loadDropdownOptions = async () => {
      try {
        // Load product categories
        const categoryResult = await import('../../../../tc-service/dtrackit.service').then(module => 
          module.dtrackItService.getProductCategory()
        );
        const categories = categoryResult.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setProductCategoryCode(categories);
        setProductCategoriesStore(categories);

        // Load product details codes
        const detailsResult = await import('../../../../tc-service/dtrackit.service').then(module => 
          module.dtrackItService.getProductDetailsCode()
        );
        const details = detailsResult.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setProductDetailsCode(details);
        setProductDetailCodeStore(details);
      } catch (err) {
        Utills.cl(`Error loading dropdown options: ${err}`);
      }
    };

    loadDropdownOptions();
  }, []);

  // Update selected rows when selectedProductIds changes
  useEffect(() => {
    const newSelectedRows = getSelectedRowIndices();
    setSelectedRows(newSelectedRows);
  }, [selectedProductIds, data]);

  // Handle row selection change
  const handleSelectionChange = (selectedIndices: number[]) => {
    setSelectedRows(selectedIndices);
    onSelectionChange(selectedIndices);
  };

  // Handle cell changes
  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    const updatedData = data.map((row: CombinedProductData, index: number) => 
      index === rowIndex ? { ...row, [columnId]: value } : row
    );
    onDataChange(updatedData);
  };

  // Handle save row
  const handleSaveRow = (updatedRow: CombinedProductData) => {
    const updatedData = data.map((row: CombinedProductData, index: number) => 
      index === editingRowIndex ? updatedRow : row
    );
    onDataChange(updatedData);
    setEditingRowIndex(null);
    setEditingRowIndices(prev => prev.filter(i => i !== editingRowIndex));
  };

  // Handle edit row
  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setEditingRowIndices(prev => [...prev, rowIndex]);
  };

  // Handle cancel edit
  const handleCancelEdit = (rowIndex: number) => {
    setEditingRowIndex(null);
    setEditingRowIndices(prev => prev.filter(i => i !== rowIndex));
  };

  // Handle delete row
  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_: CombinedProductData, index: number) => index !== rowIndex);
    onDataChange(updatedData);
    setSelectedRows(prev => prev.filter(i => i !== rowIndex));
  };


  // Column definitions - Reordered with key fields first
  const columns: EditableColumnDef<CombinedProductData>[] = [
    {
      accessorKey: 'inputTcProductNo',
      header: 'Product No.',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductNo"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Product No."
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductCategoryCode',
      header: 'Category Code',
      minSize: 140,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="inputTcProductCategoryCode"
          value={value || ''}
          options={productCategoryCode}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Select Category"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductCategoryUserSpecificTerm',
      header: 'Category Term',
      minSize: 140,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductCategoryUserSpecificTerm"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Category Term"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductDetailCode',
      header: 'Detail Code',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="inputTcProductDetailCode"
          value={value || ''}
          options={productDetailsCode}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Select Detail Code"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductDetailUserSpecificTerm',
      header: 'Detail Term',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductDetailUserSpecificTerm"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Detail Term"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductArticleNo',
      header: 'Article No.',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductArticleNo"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Article No."
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductNumberOfUnits',
      header: 'Number of Units',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductNumberOfUnits"
          type="number"
          value={value || ''}
          onChange={(e) => onChange(parseFloat(e.target.value) || null)}
          placeholder="Enter Units"
          size="small"
          inputProps={{ step: "0.01" }}
        />
      ),
    },
    {
      accessorKey: 'inputTcProductUnitsMetric',
      header: 'Units Metric',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductUnitsMetric"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Units Metric"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductProductionDate',
      header: 'Production Date',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomDatePicker
          name="inputTcProductProductionDate"
          value={value ? new Date(value) : null}
          onChange={(date) => onChange(date)}
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoFiberLength',
      header: 'Fiber Length (mm)',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoFiberLength"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Fiber Length"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoYarnCount',
      header: 'Yarn Count',
      minSize: 100,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoYarnCount"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Yarn Count"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoFabricWeightAndConstruct',
      header: 'Fabric Weight & Construct (kg)',
      minSize: 180,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoFabricWeightAndConstruct"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Fabric Weight"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoFabricWeightOfFinishedProduct',
      header: 'Finished Product Weight (kg)',
      minSize: 180,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoFabricWeightOfFinishedProduct"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Finished Weight"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoTrimsExcluded',
      header: 'Trims Excluded',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoTrimsExcluded"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Trims Info"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoAssociatedTc',
      header: 'Associated TC',
      minSize: 120,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoAssociatedTc"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Associated TC"
          size="small"
        />
      ),
    },
    {
      accessorKey: 'inputTcProductInfoAdditional',
      header: 'Additional Info',
      minSize: 150,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcProductInfoAdditional"
          multiline
          rows={3}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Additional Info"
          size="small"
        />
      ),
    },
  ];

  return (
    <>
      <Box display="flex" mb={3} justifyContent="space-between" width="100%">
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
          Product Information
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
          {selectedRows.length} of {data.length} products selected
        </Typography>
      </Box>
      <EditableTable
        data={data}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onCancelEdit={handleCancelEdit}
        onEditRow={handleEditRow}
        onDeleteRow={handleDeleteRow}
        editingRowIndices={editingRowIndices}
        enableRowSelection={true}
        selectedRowIndices={selectedRows}
        onSelectionChange={handleSelectionChange}
        cellHeight={60}
        hideDelete
      />
    </>
  );
};

export default CombinedProductDetails;
